// Encapsulates account and line-item drag-and-drop state, events, and mutation logic.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import type { SheetAccountEntry } from "../../../types/sheetDocument";
import type { LineItemDragStartPayload } from "../components/TAccountWidget";

interface DragPreviewState {
  accountId: string;
  cursorX: number;
  cursorY: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
}

interface LineItemDropTarget {
  accountId: string;
  column: "debit" | "credit";
}

interface LineItemDragState {
  sourceAccountId: string;
  sourceAccountName: string;
  sequence: number;
  displayReference: string;
  signedAmount: number;
  sourceColumn: "debit" | "credit";
  cursorX: number;
  cursorY: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
}

interface UseSheetDragAndDropParams {
  tAccounts: SheetAccountEntry[];
  setTAccounts: React.Dispatch<React.SetStateAction<SheetAccountEntry[]>>;
  buildSnapshot: () => unknown;
  pushSnapshot: (snapshot: unknown) => void;
  onMutationApplied: () => void;
}

export default function useSheetDragAndDrop({
  tAccounts,
  setTAccounts,
  buildSnapshot,
  pushSnapshot,
  onMutationApplied,
}: UseSheetDragAndDropParams) {
  const [draggedAccountId, setDraggedAccountId] = useState<string | null>(null);
  const [dragInsertIndex, setDragInsertIndex] = useState<number | null>(null);
  const [dragPreview, setDragPreview] = useState<DragPreviewState | null>(null);
  const [lineItemDrag, setLineItemDrag] = useState<LineItemDragState | null>(null);
  const [lineItemDropTarget, setLineItemDropTarget] = useState<LineItemDropTarget | null>(null);

  const tAccountLayoutRef = useRef<HTMLDivElement | null>(null);
  const tAccountRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const getOrderedAccountRects = useCallback(() => {
    return tAccounts
      .map((account, index) => {
        const node = tAccountRefs.current[account.taccountId];
        if (!node) {
          return null;
        }

        return {
          accountId: account.taccountId,
          index,
          rect: node.getBoundingClientRect(),
        };
      })
      .filter((item): item is { accountId: string; index: number; rect: DOMRect } => item !== null)
      .sort((left, right) => {
        const topDelta = left.rect.top - right.rect.top;
        if (Math.abs(topDelta) > 4) {
          return topDelta;
        }

        return left.rect.left - right.rect.left;
      });
  }, [tAccounts]);

  const calculateInsertionIndex = useCallback((clientX: number, clientY: number): number => {
    const orderedRects = getOrderedAccountRects();

    if (orderedRects.length === 0) {
      return 0;
    }

    for (let index = 0; index < orderedRects.length; index += 1) {
      const { rect } = orderedRects[index];

      if (clientY < rect.top) {
        return index;
      }

      if (clientY <= rect.bottom) {
        const midpointX = rect.left + (rect.width / 2);
        if (clientX < midpointX) {
          return index;
        }
      }
    }

    return orderedRects.length;
  }, [getOrderedAccountRects]);

  const registerTAccountRef = useCallback((accountId: string, node: HTMLDivElement | null) => {
    if (node) {
      tAccountRefs.current[accountId] = node;
      return;
    }

    delete tAccountRefs.current[accountId];
  }, []);

  const handleGripMouseDown = useCallback((accountId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const accountNode = tAccountRefs.current[accountId];
    if (!accountNode) {
      return;
    }

    const accountRect = accountNode.getBoundingClientRect();

    setDraggedAccountId(accountId);
    setDragPreview({
      accountId,
      cursorX: event.clientX,
      cursorY: event.clientY,
      offsetX: event.clientX - accountRect.left,
      offsetY: event.clientY - accountRect.top,
      width: accountRect.width,
      height: accountRect.height,
    });

    const currentIndex = tAccounts.findIndex((account) => account.taccountId === accountId);
    setDragInsertIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [tAccounts]);

  const clearDragState = useCallback(() => {
    setDraggedAccountId(null);
    setDragInsertIndex(null);
    setDragPreview(null);
  }, []);

  const calculateLineItemDropTarget = useCallback((clientX: number, clientY: number): LineItemDropTarget | null => {
    const orderedRects = getOrderedAccountRects();
    const targetAccountRect = orderedRects.find(({ rect }) => (
      clientX >= rect.left
      && clientX <= rect.right
      && clientY >= rect.top
      && clientY <= rect.bottom
    ));

    if (!targetAccountRect) {
      return null;
    }

    const midpointX = targetAccountRect.rect.left + (targetAccountRect.rect.width / 2);
    return {
      accountId: targetAccountRect.accountId,
      column: clientX < midpointX ? "debit" : "credit",
    };
  }, [getOrderedAccountRects]);

  const applyLineItemMove = useCallback((dragState: LineItemDragState, target: LineItemDropTarget | null) => {
    if (!target) {
      return;
    }

    if (target.accountId === dragState.sourceAccountId && target.column === dragState.sourceColumn) {
      return;
    }

    const previousSnapshot = buildSnapshot();
    let didApplyMove = false;

    setTAccounts((previousAccounts) => {
      let movingEntry: SheetAccountEntry["entries"][number] | null = null;

      const nextAccounts = previousAccounts.map((account) => {
        if (account.taccountId !== dragState.sourceAccountId) {
          return account;
        }

        const nextEntries = account.entries.filter((entry) => {
          if (entry.sequence === dragState.sequence) {
            movingEntry = { ...entry };
            return false;
          }

          return true;
        });

        return {
          ...account,
          entries: nextEntries,
        };
      });

      if (!movingEntry) {
        return previousAccounts;
      }

      const targetIndex = nextAccounts.findIndex((account) => account.taccountId === target.accountId);
      if (targetIndex < 0) {
        return previousAccounts;
      }

      const absoluteAmount = Math.abs(movingEntry.debitAmount ?? movingEntry.creditAmount ?? 0);
      const movedEntry = {
        ...movingEntry,
        debitAmount: target.column === "debit" ? absoluteAmount : undefined,
        creditAmount: target.column === "credit" ? absoluteAmount : undefined,
      };

      nextAccounts[targetIndex] = {
        ...nextAccounts[targetIndex],
        entries: [...nextAccounts[targetIndex].entries, movedEntry],
      };

      didApplyMove = true;
      return nextAccounts.filter((account) => account.entries.length > 0);
    });

    if (!didApplyMove) {
      return;
    }

    pushSnapshot(previousSnapshot);
    onMutationApplied();
  }, [buildSnapshot, onMutationApplied, pushSnapshot, setTAccounts]);

  const handleLineItemDragStart = useCallback((
    sourceAccountId: string,
    sourceAccountName: string,
    payload: LineItemDragStartPayload,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (draggedAccountId) {
      return;
    }

    const labelRect = event.currentTarget.getBoundingClientRect();

    setLineItemDrag({
      sourceAccountId,
      sourceAccountName,
      sequence: payload.sequence,
      displayReference: payload.displayReference,
      signedAmount: payload.signedAmount,
      sourceColumn: payload.side,
      cursorX: event.clientX,
      cursorY: event.clientY,
      offsetX: event.clientX - labelRect.left,
      offsetY: event.clientY - labelRect.top,
      width: Math.max(labelRect.width + 42, 88),
      height: Math.max(labelRect.height + 8, 24),
    });

    setLineItemDropTarget(calculateLineItemDropTarget(event.clientX, event.clientY));
  }, [calculateLineItemDropTarget, draggedAccountId]);

  const applyReorder = useCallback((accountId: string, requestedIndex: number) => {
    const currentIndex = tAccounts.findIndex((account) => account.taccountId === accountId);

    if (currentIndex < 0) {
      return;
    }

    let targetIndex = requestedIndex;
    if (targetIndex > currentIndex) {
      targetIndex -= 1;
    }

    if (targetIndex === currentIndex) {
      return;
    }

    const previousSnapshot = buildSnapshot();

    setTAccounts((previousAccounts) => {
      const draggedIndexInPrevious = previousAccounts.findIndex((account) => account.taccountId === accountId);
      if (draggedIndexInPrevious < 0) {
        return previousAccounts;
      }

      const reorderedAccounts = previousAccounts.slice();
      const [draggedAccount] = reorderedAccounts.splice(draggedIndexInPrevious, 1);
      const clampedIndex = Math.max(0, Math.min(targetIndex, reorderedAccounts.length));
      reorderedAccounts.splice(clampedIndex, 0, draggedAccount);
      return reorderedAccounts;
    });

    pushSnapshot(previousSnapshot);
    onMutationApplied();
  }, [buildSnapshot, onMutationApplied, pushSnapshot, setTAccounts, tAccounts]);

  useEffect(() => {
    if (!draggedAccountId) {
      return;
    }

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";

    const handleMouseMove = (event: MouseEvent) => {
      setDragPreview((previousPreview) => {
        if (!previousPreview) {
          return previousPreview;
        }

        return {
          ...previousPreview,
          cursorX: event.clientX,
          cursorY: event.clientY,
        };
      });

      const insertionIndex = calculateInsertionIndex(event.clientX, event.clientY);
      setDragInsertIndex((previousIndex) => (previousIndex === insertionIndex ? previousIndex : insertionIndex));
    };

    const handleMouseUp = (event: MouseEvent) => {
      const insertionIndex = calculateInsertionIndex(event.clientX, event.clientY);
      applyReorder(draggedAccountId, insertionIndex);
      clearDragState();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [applyReorder, calculateInsertionIndex, clearDragState, draggedAccountId]);

  useEffect(() => {
    if (!lineItemDrag) {
      return;
    }

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";

    const handleMouseMove = (event: MouseEvent) => {
      setLineItemDrag((previousDragState) => (
        previousDragState
          ? {
            ...previousDragState,
            cursorX: event.clientX,
            cursorY: event.clientY,
          }
          : previousDragState
      ));

      const nextTarget = calculateLineItemDropTarget(event.clientX, event.clientY);
      setLineItemDropTarget((previousTarget) => {
        if (!previousTarget && !nextTarget) {
          return previousTarget;
        }

        if (
          previousTarget
          && nextTarget
          && previousTarget.accountId === nextTarget.accountId
          && previousTarget.column === nextTarget.column
        ) {
          return previousTarget;
        }

        return nextTarget;
      });
    };

    const handleMouseUp = () => {
      applyLineItemMove(lineItemDrag, lineItemDropTarget);
      setLineItemDrag(null);
      setLineItemDropTarget(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [applyLineItemMove, calculateLineItemDropTarget, lineItemDrag, lineItemDropTarget]);

  const insertionMarkerStyle = useMemo(() => {
    if (!draggedAccountId || dragInsertIndex === null || !dragPreview) {
      return null;
    }

    const layoutNode = tAccountLayoutRef.current;
    if (!layoutNode) {
      return null;
    }

    const orderedRects = getOrderedAccountRects();
    if (orderedRects.length === 0) {
      return null;
    }

    const layoutRect = layoutNode.getBoundingClientRect();
    const markerOffset = 6;

    if (dragInsertIndex <= 0) {
      const firstRect = orderedRects[0].rect;
      return {
        left: firstRect.left - layoutRect.left + layoutNode.scrollLeft - markerOffset,
        top: firstRect.top - layoutRect.top + layoutNode.scrollTop,
        height: firstRect.height,
      };
    }

    if (dragInsertIndex >= orderedRects.length) {
      const lastRect = orderedRects[orderedRects.length - 1].rect;
      return {
        left: lastRect.right - layoutRect.left + layoutNode.scrollLeft + markerOffset,
        top: lastRect.top - layoutRect.top + layoutNode.scrollTop,
        height: lastRect.height,
      };
    }

    const nextRect = orderedRects[dragInsertIndex].rect;
    return {
      left: nextRect.left - layoutRect.left + layoutNode.scrollLeft - markerOffset,
      top: nextRect.top - layoutRect.top + layoutNode.scrollTop,
      height: nextRect.height,
    };
  }, [dragInsertIndex, dragPreview, draggedAccountId, getOrderedAccountRects]);

  const draggedAccount = useMemo(
    () => (dragPreview ? tAccounts.find((account) => account.taccountId === dragPreview.accountId) ?? null : null),
    [dragPreview, tAccounts],
  );

  return {
    tAccountLayoutRef,
    registerTAccountRef,
    handleGripMouseDown,
    handleLineItemDragStart,
    draggedAccountId,
    dragPreview,
    lineItemDrag,
    lineItemDropTarget,
    insertionMarkerStyle,
    draggedAccount,
  };
}
